// Display currency (owner decision 28 Sep): a visitor may SEE prices in their
// own currency, but the store always CHARGES AED. Conversion is display only:
// the bag total, checkout, orders, invoices and payments stay AED.
//
// Rates come from GET /settings/currencies (`rate` = units per 1 AED) and are
// fetched only once a non-AED currency is chosen, so AED visitors pay nothing.
// The choice lives in localStorage (default AED, never switched on its own).
// Kept tiny: it sits in main.js; the picker UI is the lazy CurrencyPicker.
import { useSyncExternalStore } from 'react';
import { API_URL, getJson } from './api';

const KEY = 'anum_currency';
const RATES_KEY = 'anum_currency_rates';
const DAY = 864e5;

const load = (k) => { try { return window.localStorage.getItem(k); } catch { return null; } };
const save = (k, v) => { try { window.localStorage.setItem(k, v); } catch { /* private mode */ } };

const cachedRates = () => {
  try {
    const c = JSON.parse(load(RATES_KEY));
    return c && Array.isArray(c.data?.currencies) && Date.now() - c.at < DAY ? c.data : null;
  } catch { return null; }
};

let state = { code: /^[A-Z]{3}$/.test(load(KEY) || '') ? load(KEY) : 'AED', data: cachedRates() };
let loading = null;
const subs = new Set();
const emit = () => subs.forEach((f) => f());

/** Load the rates (once per page; errors leave prices in AED). */
export const ensureRates = () => {
  if (!loading) {
    loading = getJson(`${API_URL}/settings/currencies`).then((data) => {
      if (Array.isArray(data?.currencies)) {
        state = { ...state, data };
        save(RATES_KEY, JSON.stringify({ at: Date.now(), data }));
        emit();
      }
      return data;
    }, () => { loading = null; });
  }
  return loading;
};

export const setDisplayCurrency = (code) => {
  const next = /^[A-Z]{3}$/.test(code || '') ? code : 'AED';
  save(KEY, next);
  state = { ...state, code: next };
  if (next !== 'AED') ensureRates();
  emit();
};

export const getCurrencyState = () => state;
const subscribe = (f) => { subs.add(f); return () => subs.delete(f); };

if (typeof window !== 'undefined' && state.code !== 'AED') ensureRates();

/** The chosen non-AED currency with its rate, or null (AED / not loaded / disabled). */
export const activeCurrency = (s = state) =>
  (s.code === 'AED' ? null : (s.data?.currencies || []).find((c) => c.code === s.code && c.rate > 0) || null);

/** Rounded converted amount (2 decimals; 3 for KWD/BHD/OMR; 0 for INR/PKR via `decimals`). */
export const convertAed = (aed, cur) => {
  const d = Number.isInteger(cur.decimals) ? cur.decimals : 2;
  const f = 10 ** d;
  return Math.round((Number(aed) || 0) * cur.rate * f) / f;
};

/** "1,276.50" with the currency's decimals, Latin digits in EN and AR. */
export const formatNumber = (value, decimals, lang) =>
  new Intl.NumberFormat(lang === 'ar' ? 'ar-u-nu-latn' : 'en-US', {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  }).format(value);

/** "≈ SAR 1,276.50" (EN) / "≈ 1,276.50 ر.س" (AR). */
export const formatApprox = (aed, cur, lang = 'en') => {
  const n = formatNumber(convertAed(aed, cur), cur.decimals ?? 2, lang);
  if (lang === 'ar') return `≈ ${n} ${cur.symbol_ar || cur.code}`;
  const sym = cur.symbol_en || cur.code;
  return `≈ ${sym}${sym.length > 1 ? ' ' : ''}${n}`;
};

/** React: {code, cur, data} — re-renders on a currency or rate change. */
export const useDisplayCurrency = () => {
  const s = useSyncExternalStore(subscribe, getCurrencyState, getCurrencyState);
  return { code: s.code, cur: activeCurrency(s), data: s.data };
};
